import { useGraphQLQuery } from '@opengov/bnp-core';
import { IPublicationNavMenuItem } from '@opengov/stories-shared/types';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  GetNavigationMenusForOnlinePublicationQuery,
  GetNavigationMenusForOnlinePublicationQueryVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import {
  PublicationIdKeyParams,
  setupOnlinePublicationQueryKeys
} from './setupOnlinePublicationQueryKeys';

const GetNavigationMenusForOnlinePublicationDocument = graphql(`
  query GetNavigationMenusForOnlinePublication($id: uuid!) {
    menuItems: publicationNavigationMenuItems(
      where: { publicationId: { _eq: $id } }
      order_by: [{ order: asc }, { title: asc }]
    ) {
      id
      publicationId
      title
      order
      createdAt
      createdBy
      updatedAt
      publicationNavigationMenuStories(order_by: { order: asc }) {
        id
        displayName
        menuId
        storyId
        order
        story {
          title
        }
      }
    }
  }
`);

export type UseNavigationMenusParams = PublicationIdKeyParams | null;

export function useNavigationMenus(params: UseNavigationMenusParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useGraphQLQuery<
    GetNavigationMenusForOnlinePublicationQuery,
    GetNavigationMenusForOnlinePublicationQueryVariables,
    IPublicationNavMenuItem[]
  >({
    queryKey: params
      ? setupOnlinePublicationQueryKeys.navigationMenus(params)
      : ([...setupOnlinePublicationQueryKeys.all, 'navigationMenus', 'disabled'] as const),
    document: GetNavigationMenusForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL,
    variables: params ? { id: params.publicationId } : { id: '' },
    options: {
      enabled: params !== null,
      onError: () => {
        toggleToast({ title: errorMessage, severity: 'error' });
      },
      // Codegen types every nullable column as `T | null`; IPublicationNavMenuItem
      // declares them more loosely. Runtime shape matches once Hasura returns rows.
      select: (data: GetNavigationMenusForOnlinePublicationQuery): IPublicationNavMenuItem[] =>
        (data.menuItems ?? []) as unknown as IPublicationNavMenuItem[]
    }
  });
}
